/**
 * CNPJ Validation Hook
 *
 * Provides CNPJ validation functionality for Partner Admin forms.
 * Includes format validation, Receita Federal lookup, medical practice check,
 * and duplicate detection.
 */

import { useState, useCallback } from 'react'
import { partnerAdminApi } from '../services/api'
import type { CNPJValidationResult, CNPJLookupResult, CNPJFullValidationResult, CNPJExistsResult } from '../services/api'

export interface UseCNPJValidationState {
  isValidating: boolean
  isLookingUp: boolean
  formatValidation: CNPJValidationResult | null
  lookupData: CNPJLookupResult | null
  fullValidation: CNPJFullValidationResult | null
  existsCheck: CNPJExistsResult | null
  error: string | null
}

export interface UseCNPJValidationReturn extends UseCNPJValidationState {
  validateFormat: (cnpj: string) => Promise<CNPJValidationResult | null>
  lookupCNPJ: (cnpj: string) => Promise<CNPJLookupResult | null>
  validateFull: (cnpj: string) => Promise<CNPJFullValidationResult | null>
  checkExists: (cnpj: string) => Promise<CNPJExistsResult | null>
  reset: () => void
  formatCNPJ: (cnpj: string) => string
  cleanCNPJ: (cnpj: string) => string
}

const initialState: UseCNPJValidationState = {
  isValidating: false,
  isLookingUp: false,
  formatValidation: null,
  lookupData: null,
  fullValidation: null,
  existsCheck: null,
  error: null
}

/**
 * Format CNPJ with mask: XX.XXX.XXX/XXXX-XX
 */
export function formatCNPJ(cnpj: string): string {
  const cleaned = cnpj.replace(/[^\d]/g, '')
  if (cleaned.length !== 14) return cnpj
  return cleaned.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  )
}

/**
 * Remove CNPJ formatting
 */
export function cleanCNPJ(cnpj: string): string {
  return cnpj.replace(/[^\d]/g, '')
}

/**
 * Validate CNPJ checksum locally (without API call)
 */
export function validateCNPJChecksum(cnpj: string): boolean {
  const cleaned = cleanCNPJ(cnpj)

  if (cleaned.length !== 14) return false

  // Check for known invalid patterns
  if (/^(\d)\1+$/.test(cleaned)) return false

  // Calculate first check digit
  let sum = 0
  let weight = 5
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleaned[i]) * weight
    weight = weight === 2 ? 9 : weight - 1
  }
  let remainder = sum % 11
  const digit1 = remainder < 2 ? 0 : 11 - remainder

  if (parseInt(cleaned[12]) !== digit1) return false

  // Calculate second check digit
  sum = 0
  weight = 6
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleaned[i]) * weight
    weight = weight === 2 ? 9 : weight - 1
  }
  remainder = sum % 11
  const digit2 = remainder < 2 ? 0 : 11 - remainder

  return parseInt(cleaned[13]) === digit2
}

/**
 * Hook for CNPJ validation operations
 */
export function useCNPJValidation(): UseCNPJValidationReturn {
  const [state, setState] = useState<UseCNPJValidationState>(initialState)

  const validateFormat = useCallback(async (cnpj: string): Promise<CNPJValidationResult | null> => {
    setState(prev => ({ ...prev, isValidating: true, error: null }))

    try {
      const result = await partnerAdminApi.clients.cnpj.validate(cnpj)
      setState(prev => ({
        ...prev,
        isValidating: false,
        formatValidation: result
      }))
      return result
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao validar CNPJ'
      setState(prev => ({
        ...prev,
        isValidating: false,
        error: errorMessage
      }))
      return null
    }
  }, [])

  const lookupCNPJ = useCallback(async (cnpj: string): Promise<CNPJLookupResult | null> => {
    setState(prev => ({ ...prev, isLookingUp: true, error: null }))

    try {
      const result = await partnerAdminApi.clients.cnpj.lookup(cnpj)
      setState(prev => ({
        ...prev,
        isLookingUp: false,
        lookupData: result
      }))
      return result
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao consultar CNPJ na Receita Federal'
      setState(prev => ({
        ...prev,
        isLookingUp: false,
        error: errorMessage
      }))
      return null
    }
  }, [])

  const validateFull = useCallback(async (cnpj: string): Promise<CNPJFullValidationResult | null> => {
    setState(prev => ({ ...prev, isValidating: true, isLookingUp: true, error: null }))

    try {
      const result = await partnerAdminApi.clients.cnpj.validateFull(cnpj)
      setState(prev => ({
        ...prev,
        isValidating: false,
        isLookingUp: false,
        fullValidation: result,
        lookupData: result.rfData || null
      }))
      return result
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao validar CNPJ'
      setState(prev => ({
        ...prev,
        isValidating: false,
        isLookingUp: false,
        error: errorMessage
      }))
      return null
    }
  }, [])

  const checkExists = useCallback(async (cnpj: string): Promise<CNPJExistsResult | null> => {
    setState(prev => ({ ...prev, isValidating: true, error: null }))

    try {
      const result = await partnerAdminApi.clients.cnpj.checkExists(cnpj)
      setState(prev => ({
        ...prev,
        isValidating: false,
        existsCheck: result
      }))
      return result
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao verificar CNPJ'
      setState(prev => ({
        ...prev,
        isValidating: false,
        error: errorMessage
      }))
      return null
    }
  }, [])

  const reset = useCallback(() => {
    setState(initialState)
  }, [])

  return {
    ...state,
    validateFormat,
    lookupCNPJ,
    validateFull,
    checkExists,
    reset,
    formatCNPJ,
    cleanCNPJ
  }
}

export default useCNPJValidation
